{
  "pages": [
    {
      "guideType": "docs",
      "slug": "business",
      "title": "Gab AI Business",
      "description": "Centralized billing, team seats, shared credits, and the normal Gab AI dashboard.",
      "badge": "Business",
      "badgeColor": "#123524",
      "navSectionSlug": "business",
      "navOrder": 0,
      "navIcon": "building",
      "searchKeywords": [
        "business",
        "team",
        "company",
        "seats",
        "shared credits"
      ],
      "meta": {
        "title": "Gab AI Business | Gab AI Documentation",
        "description": "Centralized billing, team seats, shared credits, and the normal Gab AI dashboard.",
        "keywords": "business, team, company, seats, shared credits",
        "pageCopy": [
          {
            "type": "tip",
            "tipType": "info",
            "title": "Availability",
            "copy": "Gab AI Business is set up from Settings → Business on the website, with an account that has a verified email address."
          },
          {
            "type": "section",
            "title": "One dashboard, company billing",
            "copy": "Business gives your team Plus and Plus Premium seats and a shared credit pool. Existing paid plans can cover the owner’s seat while keeping their original billing. The owner’s remaining included credits join the pool; purchased personal credits stay personal. Additional seats are billed to the company."
          },
          {
            "type": "section",
            "title": "What Business includes",
            "copy": "Business supports monthly billing and teams of at least two, including the owner. Mix Plus and Plus Premium seats. Members use normal Gab AI features with their assigned plan and company allowance, subject to company and per-member limits. Owners and admins get usage reports with CSV export, member limit requests, and an activity log. Annual company plans, automatic top-up, SSO, and shared company API keys are not available."
          },
          {
            "type": "section",
            "title": "Personal and Business",
            "copy": "Keep using the normal Gab AI dashboard, with your private chats, files, and memory. Company credits pay for usage by default. You can choose personal credits in Settings → Gab AI Business without losing the access provided by your seat. Purchased personal credits stay separate. If an existing plan covers the owner’s seat, its unused included credits join the company pool."
          },
          {
            "type": "section",
            "title": "Where to set up Business",
            "copy": "Create or upgrade to Business on the website. This includes people who currently subscribe through Apple or Google. Your Business seat applies to your existing Gab AI account. Company setup, upgrades, and billing controls are on the website."
          },
          {
            "type": "quick-links",
            "title": "Business guides",
            "items": [
              {
                "title": "Set up a business",
                "href": "/docs/business-setup",
                "description": "Website setup, seat selection, and team invitation flow."
              },
              {
                "title": "Upgrade an existing account",
                "href": "/docs/business-upgrade",
                "description": "Use the same account for Business, including current Apple and Google subscribers."
              },
              {
                "title": "Members and invitations",
                "href": "/docs/business-members",
                "description": "Roles, paid seats, invitations, and member access controls."
              },
              {
                "title": "Business billing",
                "href": "/docs/business-billing",
                "description": "Monthly seat billing, renewals, changes, credit packs, and cancellation."
              },
              {
                "title": "Shared credits and limits",
                "href": "/docs/business-credits",
                "description": "Company credit contributions, included and purchased balances, pending usage, and limits."
              },
              {
                "title": "Privacy and company access",
                "href": "/docs/business-privacy",
                "description": "Admin visibility, personal data separation, sharing, and offboarding."
              }
            ]
          }
        ]
      },
      "guideSeo": {
        "readMinutes": 3,
        "difficulty": "Beginner",
        "articleSection": "Gab AI Business"
      },
      "navigation": {
        "next": {
          "label": "Set up a business",
          "href": "/docs/business-setup"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/business",
      "updatedAt": "2026-09-30T03:00:00.000Z",
      "publishedAt": "2026-09-28T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "business-setup",
      "title": "Set up a business",
      "description": "Website setup, seat selection, and team invitation flow.",
      "badge": "Business",
      "badgeColor": "#123524",
      "navSectionSlug": "business",
      "navOrder": 1,
      "navIcon": "building",
      "searchKeywords": [
        "business",
        "setup",
        "create",
        "company",
        "invite",
        "web"
      ],
      "meta": {
        "title": "Set up a business | Gab AI Documentation",
        "description": "Website setup, seat selection, and team invitation flow.",
        "keywords": "business, setup, create, company, invite, web",
        "pageCopy": [
          {
            "type": "tip",
            "tipType": "info",
            "title": "Availability",
            "copy": "Gab AI Business is set up from Settings → Business on the website, with an account that has a verified email address."
          },
          {
            "type": "section",
            "title": "Use the website",
            "copy": "Sign in to Gab AI in a browser using your existing account, or create an account, and verify your email address. A browser on your phone also qualifies. Open Settings → Business to begin. Business setup is available only on the website."
          },
          {
            "type": "section",
            "title": "Choose seats and review the bill",
            "copy": "Name the company, choose the owner’s seat, and select Plus and Plus Premium quantities for the team. At least two paid seats are required in total, including the owner. A free account buys two company seats. If you already have a paid Plus or Plus Premium plan, keep its tier and use it for your owner seat, then buy at least one additional seat of either tier. The review screen shows company-billed seats and their shared credits before payment."
          },
          {
            "type": "section",
            "title": "Invite your team",
            "copy": "After payment is confirmed, invite teammates into the available paid seats. Assign a seat tier, role, and usage limit. Invitees sign in with the verified email address on the invitation and accept the named company. Invitations use already-paid capacity and do not increase the bill."
          },
          {
            "type": "section",
            "title": "Choose who pays for your usage",
            "copy": "Use Chat, Work, and other features from the normal dashboard. Company allowance is the default. Settings → Gab AI Business lets you select personal credits or a specific company. Running requests keep their original payer; a new request uses your saved choice. Your history stays private in your account."
          },
          {
            "type": "quick-links",
            "title": "Business guides",
            "items": [
              {
                "title": "Gab AI Business",
                "href": "/docs/business",
                "description": "Centralized billing, team seats, shared credits, and the normal Gab AI dashboard."
              },
              {
                "title": "Upgrade an existing account",
                "href": "/docs/business-upgrade",
                "description": "Use the same account for Business, including current Apple and Google subscribers."
              },
              {
                "title": "Members and invitations",
                "href": "/docs/business-members",
                "description": "Roles, paid seats, invitations, and member access controls."
              },
              {
                "title": "Business billing",
                "href": "/docs/business-billing",
                "description": "Monthly seat billing, renewals, changes, credit packs, and cancellation."
              },
              {
                "title": "Shared credits and limits",
                "href": "/docs/business-credits",
                "description": "Company credit contributions, included and purchased balances, pending usage, and limits."
              },
              {
                "title": "Privacy and company access",
                "href": "/docs/business-privacy",
                "description": "Admin visibility, personal data separation, sharing, and offboarding."
              }
            ]
          }
        ]
      },
      "guideSeo": {
        "readMinutes": 3,
        "difficulty": "Beginner",
        "articleSection": "Gab AI Business"
      },
      "navigation": {
        "previous": {
          "label": "Gab AI Business",
          "href": "/docs/business"
        },
        "next": {
          "label": "Upgrade an existing account",
          "href": "/docs/business-upgrade"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/business-setup",
      "updatedAt": "2026-09-30T03:00:00.000Z",
      "publishedAt": "2026-09-28T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "business-upgrade",
      "title": "Upgrade an existing account",
      "description": "Use the same account for Business, including current Apple and Google subscribers.",
      "badge": "Business",
      "badgeColor": "#123524",
      "navSectionSlug": "business",
      "navOrder": 2,
      "navIcon": "arrow-up",
      "searchKeywords": [
        "business",
        "upgrade",
        "apple",
        "google",
        "store",
        "personal",
        "subscription"
      ],
      "meta": {
        "title": "Upgrade an existing account | Gab AI Documentation",
        "description": "Use the same account for Business, including current Apple and Google subscribers.",
        "keywords": "business, upgrade, apple, google, store, personal, subscription",
        "pageCopy": [
          {
            "type": "tip",
            "tipType": "info",
            "title": "Availability",
            "copy": "Gab AI Business is set up from Settings → Business on the website, with an account that has a verified email address."
          },
          {
            "type": "section",
            "title": "Keep the same login",
            "copy": "Existing Gab AI users can create a Business account on the website. Your account becomes the company owner, and your chosen seat counts toward the company’s paid seats. Your personal chats, files, memory, and purchased credits stay in your account."
          },
          {
            "type": "section",
            "title": "Subscribed through Apple or Google",
            "copy": "Start Business on the website without waiting for your store subscription to expire. Your active paid store plan covers the owner seat; buy at least one additional seat of either tier. Your Apple or Google subscription continues with its original billing. Remaining included credits join the shared pool and refresh on the original schedule. Purchased personal credits stay yours."
          },
          {
            "type": "section",
            "title": "Manage the store subscription",
            "copy": "Keep the store subscription active while it funds your owner seat. Cancellation preserves already-paid coverage through the store’s deadline; expiration or revocation pauses company paid activity until you restore the owner plan. Store renewals update coverage automatically. Apple and Google subscriptions are still managed through their respective stores. Business setup does not cancel them or promise a store refund."
          },
          {
            "type": "section",
            "title": "Eligible monthly web subscriptions",
            "copy": "Eligible monthly card subscribers can also review combining their subscription into company billing. That optional conversion accounts for remaining paid coverage and unused included credits once. Only the reviewed extra seats or tier changes incur an additional charge. Keeping the original subscription and buying additional company seats is also available."
          },
          {
            "type": "section",
            "title": "Other subscriptions and invitations",
            "copy": "Annual, prepaid Bitcoin, Whop, weekly, discounted, and legacy paid Plus and Plus Premium plans can cover the owner’s seat. Keep the original price and billing terms; only additional seats enter company billing. Remaining included credits join the shared pool and keep their existing refresh schedule and expiry. One personal plan covers one company. Free accounts buy two company seats."
          },
          {
            "type": "quick-links",
            "title": "Related guidance",
            "items": [
              {
                "title": "Apple subscription management",
                "href": "https://support.apple.com/118428"
              },
              {
                "title": "Google Play subscription management",
                "href": "https://support.google.com/googleplay/answer/7018481"
              }
            ]
          },
          {
            "type": "quick-links",
            "title": "Business guides",
            "items": [
              {
                "title": "Gab AI Business",
                "href": "/docs/business",
                "description": "Centralized billing, team seats, shared credits, and the normal Gab AI dashboard."
              },
              {
                "title": "Set up a business",
                "href": "/docs/business-setup",
                "description": "Website setup, seat selection, and team invitation flow."
              },
              {
                "title": "Members and invitations",
                "href": "/docs/business-members",
                "description": "Roles, paid seats, invitations, and member access controls."
              },
              {
                "title": "Business billing",
                "href": "/docs/business-billing",
                "description": "Monthly seat billing, renewals, changes, credit packs, and cancellation."
              },
              {
                "title": "Shared credits and limits",
                "href": "/docs/business-credits",
                "description": "Company credit contributions, included and purchased balances, pending usage, and limits."
              },
              {
                "title": "Privacy and company access",
                "href": "/docs/business-privacy",
                "description": "Admin visibility, personal data separation, sharing, and offboarding."
              }
            ]
          }
        ]
      },
      "guideSeo": {
        "readMinutes": 3,
        "difficulty": "Beginner",
        "articleSection": "Gab AI Business"
      },
      "navigation": {
        "previous": {
          "label": "Set up a business",
          "href": "/docs/business-setup"
        },
        "next": {
          "label": "Members and invitations",
          "href": "/docs/business-members"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/business-upgrade",
      "updatedAt": "2026-09-30T03:00:00.000Z",
      "publishedAt": "2026-09-28T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "business-members",
      "title": "Members and invitations",
      "description": "Roles, paid seats, invitations, and member access controls.",
      "badge": "Business",
      "badgeColor": "#123524",
      "navSectionSlug": "business",
      "navOrder": 3,
      "navIcon": "users",
      "searchKeywords": [
        "business",
        "members",
        "invites",
        "team",
        "roles",
        "seats",
        "owner",
        "admin"
      ],
      "meta": {
        "title": "Members and invitations | Gab AI Documentation",
        "description": "Roles, paid seats, invitations, and member access controls.",
        "keywords": "business, members, invites, team, roles, seats, owner, admin",
        "pageCopy": [
          {
            "type": "tip",
            "tipType": "info",
            "title": "Availability",
            "copy": "Gab AI Business is set up from Settings → Business on the website, with an account that has a verified email address."
          },
          {
            "type": "section",
            "title": "Roles and seats",
            "copy": "Owners manage company billing and paid capacity. Admins help manage members, invitations, and permitted usage limits. Members use the company’s enabled features within their limits. A person’s role is separate from their Plus or Plus Premium seat. One Premium seat does not give Premium entitlements to every member."
          },
          {
            "type": "section",
            "title": "Invite into paid capacity",
            "copy": "A pending invitation reserves a vacant seat of its selected tier. Invitees receive a private email link and must sign in with the invited, verified email. Owners and permitted admins can check email status, resend an invitation, or share its private link. Invitations expire after seven days and can be revoked. Resending keeps the original link, seat reservation, and expiry. Accepting claims the reserved seat without purchasing another seat. The company must have active paid capacity when the invitation is accepted."
          },
          {
            "type": "section",
            "title": "Reuse seats",
            "copy": "Removing or suspending a member stops their access. Their paid seat remains available for reassignment through the paid period. Reassignment does not create a charge or another credit grant. To reduce the next bill, the owner must also schedule a lower paid seat quantity."
          },
          {
            "type": "section",
            "title": "Remove access and review pending work",
            "copy": "Removing a member blocks new company work and requests cancellation of active work. Charges for work already dispatched may still settle against the original company reservation. Review pending usage when offboarding."
          },
          {
            "type": "section",
            "title": "Ownership",
            "copy": "A company must retain at least one active owner. Changing billing responsibility requires an explicit handover; granting someone an owner role does not transfer another person’s saved-card authorization. Ownership transfer is not available in the app yet; contact ai@gab.com if the owner must change."
          },
          {
            "type": "quick-links",
            "title": "Business guides",
            "items": [
              {
                "title": "Gab AI Business",
                "href": "/docs/business",
                "description": "Centralized billing, team seats, shared credits, and the normal Gab AI dashboard."
              },
              {
                "title": "Set up a business",
                "href": "/docs/business-setup",
                "description": "Website setup, seat selection, and team invitation flow."
              },
              {
                "title": "Upgrade an existing account",
                "href": "/docs/business-upgrade",
                "description": "Use the same account for Business, including current Apple and Google subscribers."
              },
              {
                "title": "Business billing",
                "href": "/docs/business-billing",
                "description": "Monthly seat billing, renewals, changes, credit packs, and cancellation."
              },
              {
                "title": "Shared credits and limits",
                "href": "/docs/business-credits",
                "description": "Company credit contributions, included and purchased balances, pending usage, and limits."
              },
              {
                "title": "Privacy and company access",
                "href": "/docs/business-privacy",
                "description": "Admin visibility, personal data separation, sharing, and offboarding."
              }
            ]
          }
        ]
      },
      "guideSeo": {
        "readMinutes": 3,
        "difficulty": "Beginner",
        "articleSection": "Gab AI Business"
      },
      "navigation": {
        "previous": {
          "label": "Upgrade an existing account",
          "href": "/docs/business-upgrade"
        },
        "next": {
          "label": "Business billing",
          "href": "/docs/business-billing"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/business-members",
      "updatedAt": "2026-09-28T12:00:00.000Z",
      "publishedAt": "2026-09-28T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "business-billing",
      "title": "Business billing",
      "description": "Monthly seat billing, renewals, changes, credit packs, and cancellation.",
      "badge": "Business",
      "badgeColor": "#123524",
      "navSectionSlug": "business",
      "navOrder": 4,
      "navIcon": "credit-card",
      "searchKeywords": [
        "business",
        "billing",
        "subscription",
        "invoice",
        "price",
        "seats",
        "payment",
        "cancel"
      ],
      "meta": {
        "title": "Business billing | Gab AI Documentation",
        "description": "Monthly seat billing, renewals, changes, credit packs, and cancellation.",
        "keywords": "business, billing, subscription, invoice, price, seats, payment, cancel",
        "pageCopy": [
          {
            "type": "tip",
            "tipType": "info",
            "title": "Availability",
            "copy": "Gab AI Business is set up from Settings → Business on the website, with an account that has a verified email address."
          },
          {
            "type": "section",
            "title": "Monthly seats",
            "copy": "Each Plus and Plus Premium seat uses that tier’s current individual monthly price and monthly credit contribution. Business adds centralized billing and a company credit pool. The company has one monthly cycle and a consolidated total for the chosen seat quantities. Setup, purchases, upgrades, and billing management are on the website, in Settings → Business → Billing."
          },
          {
            "type": "section",
            "title": "Payment method and international billing",
            "copy": "Where available, owners outside the United States can pay through Whop's secure hosted checkout instead of entering a card in Gab. Business is billed in US dollars either way. Whop-billed companies renew every 30 days rather than on a calendar-month date, and the Billing page shows the exact period. Seat changes, credit packs, and cancellation work the same way, using the payment method saved with Whop."
          },
          {
            "type": "section",
            "title": "Adding seats and upgrading tiers",
            "copy": "Owners review the price before adding paid seats or upgrading a paid seat from Plus to Plus Premium. Mid-period changes charge the prorated fee or price difference and contribute the same prorated share of credits after confirmed payment. Assigning a member to already-paid vacant capacity does not charge again."
          },
          {
            "type": "section",
            "title": "Reductions and cancellation",
            "copy": "Seat reductions and tier downgrades take effect at renewal. Removing a member alone does not lower the subscription quantity. Cancellation stops the next renewal while retaining access through the paid period. The exact paid-through date and scheduled quantities appear in Billing."
          },
          {
            "type": "section",
            "title": "Credits and receipts",
            "copy": "Company-billed seats refresh on the company’s monthly anniversary. A linked owner plan keeps its original included-credit refresh schedule. Owners can buy additional credits from the Business Overview or Billing. Credit purchases and subscription payments appear in billing records; credit consumption is usage, not another cash invoice."
          },
          {
            "type": "section",
            "title": "Payment problems",
            "copy": "A failed renewal stops new billable company activity when paid coverage ends. A failed credit purchase adds no credits and does not remove already-funded access. The owner sees recovery options in Billing on the website. If a company payment is refunded, voided, returned, or disputed, company billing is paused while we review it: new requests and seat changes are unavailable, existing chats and files stay visible, and support at ai@gab.com resolves it with the owner. Refunds, disputes, and payment recovery are governed by the Terms of Sale and the Refund Policy."
          },
          {
            "type": "section",
            "title": "Personal subscriptions",
            "copy": "Joining a company does not cancel personal billing or move a teammate’s personal credits. If your existing paid plan covers the owner’s seat, its billing stays with the original provider and its unused included credits join the company pool. Read the existing-account guide before setup."
          },
          {
            "type": "quick-links",
            "title": "Related guidance",
            "items": [
              {
                "title": "existing-account guide",
                "href": "/docs/business-upgrade"
              },
              {
                "title": "Terms of Sale",
                "href": "/terms-of-sale"
              },
              {
                "title": "Refund Policy",
                "href": "/refund-policy"
              }
            ]
          },
          {
            "type": "quick-links",
            "title": "Business guides",
            "items": [
              {
                "title": "Gab AI Business",
                "href": "/docs/business",
                "description": "Centralized billing, team seats, shared credits, and the normal Gab AI dashboard."
              },
              {
                "title": "Set up a business",
                "href": "/docs/business-setup",
                "description": "Website setup, seat selection, and team invitation flow."
              },
              {
                "title": "Upgrade an existing account",
                "href": "/docs/business-upgrade",
                "description": "Use the same account for Business, including current Apple and Google subscribers."
              },
              {
                "title": "Members and invitations",
                "href": "/docs/business-members",
                "description": "Roles, paid seats, invitations, and member access controls."
              },
              {
                "title": "Shared credits and limits",
                "href": "/docs/business-credits",
                "description": "Company credit contributions, included and purchased balances, pending usage, and limits."
              },
              {
                "title": "Privacy and company access",
                "href": "/docs/business-privacy",
                "description": "Admin visibility, personal data separation, sharing, and offboarding."
              }
            ]
          }
        ]
      },
      "guideSeo": {
        "readMinutes": 3,
        "difficulty": "Beginner",
        "articleSection": "Gab AI Business"
      },
      "navigation": {
        "previous": {
          "label": "Members and invitations",
          "href": "/docs/business-members"
        },
        "next": {
          "label": "Shared credits and limits",
          "href": "/docs/business-credits"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/business-billing",
      "updatedAt": "2026-09-28T12:00:00.000Z",
      "publishedAt": "2026-09-28T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "business-credits",
      "title": "Shared credits and limits",
      "description": "Company credit contributions, included and purchased balances, pending usage, and limits.",
      "badge": "Business",
      "badgeColor": "#123524",
      "navSectionSlug": "business",
      "navOrder": 5,
      "navIcon": "coins",
      "searchKeywords": [
        "business",
        "credits",
        "shared",
        "pool",
        "limit",
        "budget",
        "usage",
        "pending",
        "refund"
      ],
      "meta": {
        "title": "Shared credits and limits | Gab AI Documentation",
        "description": "Company credit contributions, included and purchased balances, pending usage, and limits.",
        "keywords": "business, credits, shared, pool, limit, budget, usage, pending, refund",
        "pageCopy": [
          {
            "type": "tip",
            "tipType": "info",
            "title": "Availability",
            "copy": "Gab AI Business is set up from Settings → Business on the website, with an account that has a verified email address."
          },
          {
            "type": "section",
            "title": "One company pool",
            "copy": "Every paid seat contributes included credits to the shared company balance. If an existing plan covers the owner, its remaining included credits move into the pool and keep their original refresh schedule. Purchased personal credits stay in the owner’s account. A company seat’s contribution is not a member’s fixed quota: a member may use more or less, within the company’s and their own limits. Setup shows the actual company bill and shared credit contribution."
          },
          {
            "type": "section",
            "title": "Included and purchased credits",
            "copy": "Company-billed seats refresh on the company billing anniversary. A linked owner plan’s included credits keep its existing refresh schedule. Included credits expire at their own refresh dates and are used first. Purchased company credits carry forward; purchased personal credits stay in your account."
          },
          {
            "type": "section",
            "title": "Three spending controls",
            "copy": "Every billable action must fit the available company balance, the company’s monthly usage ceiling, and the member’s monthly usage ceiling. Limits include both completed usage and pending reservations. A higher member limit cannot bypass company funds or the company limit."
          },
          {
            "type": "section",
            "title": "Pending and completed usage",
            "copy": "A request reserves a maximum credit amount before paid work starts. Reliable final usage settles the charge and releases the unused amount. Uncertain completion stays pending until reconciled. Refunds return to the original company and credit source; expired included credits do not become new permanent credits."
          },
          {
            "type": "section",
            "title": "When a limit is reached",
            "copy": "New paid work pauses when company credits or a spending limit are exhausted. The message identifies the relevant limit. A higher member limit still requires available company credits. Company work never silently charges a personal balance or card."
          },
          {
            "type": "section",
            "title": "Request a higher member limit",
            "copy": "In Business settings on the website, open Requests. Members with a monthly limit can also open Request a higher credit limit in company Chat or Work on supported clients. Enter the requested monthly total and a reason. Each member can have one pending request and can withdraw it to make a different request. Requests expire after seven days."
          },
          {
            "type": "section",
            "title": "Review requests on the website",
            "copy": "Owners review requests in Business settings → Requests. Admins can review requests from ordinary members; an owner reviews an admin’s request. Approval updates the member’s monthly limit using the existing company pool. It does not buy credits, add seats, or change billing. A request cannot override a newer change to the member’s limit or access."
          },
          {
            "type": "section",
            "title": "Whole credit displays",
            "copy": "Business screens round displayed credit amounts up to the next whole credit. Usage accounting and CSV exports retain their exact amounts."
          },
          {
            "type": "section",
            "title": "Usage reports",
            "copy": "Owners and admins see member, feature, model, time, and credit totals, including pending usage, in Settings → Business → Usage, and can export them as CSV. They can use this to explain spending without reading private prompts, conversations, files, or outputs."
          },
          {
            "type": "quick-links",
            "title": "Business guides",
            "items": [
              {
                "title": "Gab AI Business",
                "href": "/docs/business",
                "description": "Centralized billing, team seats, shared credits, and the normal Gab AI dashboard."
              },
              {
                "title": "Set up a business",
                "href": "/docs/business-setup",
                "description": "Website setup, seat selection, and team invitation flow."
              },
              {
                "title": "Upgrade an existing account",
                "href": "/docs/business-upgrade",
                "description": "Use the same account for Business, including current Apple and Google subscribers."
              },
              {
                "title": "Members and invitations",
                "href": "/docs/business-members",
                "description": "Roles, paid seats, invitations, and member access controls."
              },
              {
                "title": "Business billing",
                "href": "/docs/business-billing",
                "description": "Monthly seat billing, renewals, changes, credit packs, and cancellation."
              },
              {
                "title": "Privacy and company access",
                "href": "/docs/business-privacy",
                "description": "Admin visibility, personal data separation, sharing, and offboarding."
              }
            ]
          }
        ]
      },
      "guideSeo": {
        "readMinutes": 3,
        "difficulty": "Beginner",
        "articleSection": "Gab AI Business"
      },
      "navigation": {
        "previous": {
          "label": "Business billing",
          "href": "/docs/business-billing"
        },
        "next": {
          "label": "Privacy and company access",
          "href": "/docs/business-privacy"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/business-credits",
      "updatedAt": "2026-09-30T03:00:00.000Z",
      "publishedAt": "2026-09-28T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "business-privacy",
      "title": "Privacy and company access",
      "description": "Admin visibility, personal data separation, sharing, and offboarding.",
      "badge": "Business",
      "badgeColor": "#123524",
      "navSectionSlug": "business",
      "navOrder": 6,
      "navIcon": "lock",
      "searchKeywords": [
        "business",
        "privacy",
        "personal",
        "sharing",
        "admin",
        "history",
        "files",
        "data",
        "offboarding"
      ],
      "meta": {
        "title": "Privacy and company access | Gab AI Documentation",
        "description": "Admin visibility, personal data separation, sharing, and offboarding.",
        "keywords": "business, privacy, personal, sharing, admin, history, files, data, offboarding",
        "pageCopy": [
          {
            "type": "tip",
            "tipType": "info",
            "title": "Availability",
            "copy": "Gab AI Business is set up from Settings → Business on the website, with an account that has a verified email address."
          },
          {
            "type": "section",
            "title": "Usage oversight",
            "copy": "Company owners and admins need usage information to manage spending. Usage reports show who used credits, when, on which feature or model, and how much was used or remains pending. A billing or admin role alone does not open another member’s private prompts, chats, files, or outputs."
          },
          {
            "type": "section",
            "title": "Your existing personal data",
            "copy": "Creating or joining a business adds a paid seat to your existing Gab AI account. Your history, files, and memory stay private. Purchased personal credits remain separate. If your existing plan covers the owner’s seat, its unused included credits join the company pool. Joining as a teammate does not move your personal credits or cancel your subscription."
          },
          {
            "type": "section",
            "title": "Sharing company work",
            "copy": "Chats and tasks stay private to the person who created them. Business does not add company-wide sharing of chats, files, or tasks. Changing your billing source does not move content or expand access."
          },
          {
            "type": "section",
            "title": "Connected accounts",
            "copy": "Business does not provide shared company connectors. Personal connector credentials and browser sessions stay with the account that connected them and are not shared with the company."
          },
          {
            "type": "section",
            "title": "Leaving a company",
            "copy": "Removal ends the Business seat and blocks new company-funded usage. Already-dispatched work can still generate pending charges. The member keeps their account and private history. Company usage and activity records remain available to owners and admins for accounting. A billing-role change does not delete or export anyone’s data. Contact ai@gab.com for data access or deletion requests."
          },
          {
            "type": "section",
            "title": "Current privacy terms",
            "copy": "The Gab AI privacy policy describes current service practices. Business adds the owner and admin usage visibility described above. These guides do not promise SSO, a separate data-processing agreement, or provider-specific training exclusions."
          },
          {
            "type": "quick-links",
            "title": "Related guidance",
            "items": [
              {
                "title": "Gab AI privacy policy",
                "href": "/privacy-policy"
              }
            ]
          },
          {
            "type": "quick-links",
            "title": "Business guides",
            "items": [
              {
                "title": "Gab AI Business",
                "href": "/docs/business",
                "description": "Centralized billing, team seats, shared credits, and the normal Gab AI dashboard."
              },
              {
                "title": "Set up a business",
                "href": "/docs/business-setup",
                "description": "Website setup, seat selection, and team invitation flow."
              },
              {
                "title": "Upgrade an existing account",
                "href": "/docs/business-upgrade",
                "description": "Use the same account for Business, including current Apple and Google subscribers."
              },
              {
                "title": "Members and invitations",
                "href": "/docs/business-members",
                "description": "Roles, paid seats, invitations, and member access controls."
              },
              {
                "title": "Business billing",
                "href": "/docs/business-billing",
                "description": "Monthly seat billing, renewals, changes, credit packs, and cancellation."
              },
              {
                "title": "Shared credits and limits",
                "href": "/docs/business-credits",
                "description": "Company credit contributions, included and purchased balances, pending usage, and limits."
              }
            ]
          }
        ]
      },
      "guideSeo": {
        "readMinutes": 3,
        "difficulty": "Beginner",
        "articleSection": "Gab AI Business"
      },
      "navigation": {
        "previous": {
          "label": "Shared credits and limits",
          "href": "/docs/business-credits"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/business-privacy",
      "updatedAt": "2026-09-28T12:00:00.000Z",
      "publishedAt": "2026-09-28T12:00:00.000Z"
    }
  ]
}
